import { useEffect, useState } from 'react';

/**
 * Swap the page's manifest to Fair Tracker's while the app is open, and expose
 * the browser's install prompt.
 *
 * WHY SWAP THE MANIFEST: BeeNexa's own manifest has `start_url: "/"`, so
 * installing from inside Fair Tracker would put a "BeeNexa" icon on the home
 * screen that opens the marketplace. The buyer needs a "Fair Tracker" icon that
 * opens straight into the app — on the show floor, two taps to get past a
 * marketplace is two taps too many.
 *
 * The swap is reverted on unmount so the rest of BeeNexa keeps its own identity.
 */
export default function useInstallable() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const previous = link ? link.getAttribute('href') : null;
    if (link) link.setAttribute('href', '/manifest-fairtracker.json');

    const onPrompt = (e) => {
      // Chrome fires this when the app is installable; holding the event lets
      // us offer our own button instead of relying on the browser's menu.
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      if (link && previous) link.setAttribute('href', previous);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const choice = await prompt.userChoice;
    setPrompt(null);
    return choice && choice.outcome === 'accepted';
  };

  return {
    /** True when the browser has offered an install prompt we can trigger. */
    canInstall: !!prompt,
    installed,
    install,
  };
}
